<script setup>
import useGrid from "@/composables/useGrid";
import projectsTasksService from "@/services/projects-tasks.service";
import {ref, inject, provide, watch, computed} from "vue";
import constants from "@/constants/constants";
import PulseLoader from "vue-spinner/src/MoonLoader.vue";
import TablePager from "@/components/TablePager.vue";
import TaskItem from "@/views/projects/TaskItem.vue";
import useTrigger from "@/composables/useTrigger";
import {getNumberWithNoun} from "../../helpers/string";

const {gridParams, gridMethods, items, listLoaded, listLoading} = useGrid(getTasksMethod, 20);
const emit = defineEmits(['showTask']);
const [selectAll, setSelectAll] = useTrigger();
const [deSelectAll, setDeSelectAll] = useTrigger();

const project = inject('tracker').value;
const serverFilters = inject("serverFilters");
const tasksListUpdated = inject("tasksListUpdated");
const setTasksListUpdated = inject("setTasksListUpdated");
const filteredTasksCount = inject("filteredCompaniesCount");
const filtersApplied = inject("filtersApplied");
const addTask = inject("addTask");
const selectedIds = ref([]);

const selectedAll = computed(() => {
    return items.value.length > 0 && selectedIds.value.length === items.value.length;
});

watch(tasksListUpdated, (value) => {
    if (value) {
        getTasks();
    }
});

watch(serverFilters, () => {
    getTasks();
}, { immediate: true });

function getTasksMethod(gridParams) {
    return projectsTasksService.getList(project.id, gridParams);
}

async function getTasks() {
    gridParams.filters = serverFilters.value;
    await gridMethods.updateList();

    if (filtersApplied.value) {
        filteredTasksCount.value = gridParams.totalRowsCount;
    } else {
        filteredTasksCount.value = null;
    }
}

async function deleteSelectedTasks() {
    if (!confirm('Delete selected tasks?')) {
        return;
    }

    await projectsTasksService.deleteTasks({ids: selectedIds.value});

    setTasksListUpdated();
    selectedIds.value = [];
}

function setSelected(taskId, selected) {
    if (selected) {
        if (!selectedIds.value.includes(taskId)) {
            selectedIds.value.push(taskId);
        }
    } else {
        selectedIds.value = selectedIds.value.filter(id => id !== taskId);
    }
}

provide('selectAll', selectAll);
provide('deSelectAll', deSelectAll);
</script>

<template>
    <div class="project-table main-table-wrapper main-table-wrapper--light">
        <PulseLoader :loading="listLoading" :color="constants.spinnerColor"/>

        <template v-if="listLoaded">
            <template v-if="items.length">
                <table class="project-table__table main-table">
                    <thead>
                    <tr>
                        <th>&nbsp;</th>
                        <th class="pl-0">
                            <div class="project-table__table__controls">
                                <span>
                                    <a v-if="selectedAll" class="link" @click="setDeSelectAll">De-select All</a>
                                    <a v-else class="link" @click="setSelectAll">Select All</a>
                                </span>

                                <span>
                                    <a
                                        v-if="selectedIds.length"
                                        class="link status-rejected"
                                        @click="deleteSelectedTasks"
                                    >
                                        Delete {{getNumberWithNoun('Task', selectedIds.length)}}
                                    </a>
                                </span>
                            </div>
                        </th>
                        <th>
                            &nbsp;
                        </th>
                        <th>Priority</th>
                        <th>Status</th>
                        <th>Reporter</th>
                        <th>Due Date</th>
                        <th>Company</th>
                        <th>&nbsp;</th>
                    </tr>
                    </thead>

                    <tbody>
                    <template v-for="task in items">
                        <tr class="main-table__empty-row" :key="'empty-row-'+task.id">
                            <td colspan="100%"></td>
                        </tr>

                        <TaskItem
                            :task="task"
                            :key="task.id"
                            @showTask="emit('showTask', task.id)"
                            @selected="(selected) => setSelected(task.id, selected)"
                        />
                    </template>

                    <tr class="main-table__empty-row"><td colspan="100%"></td></tr>
                    <tr>
                        <td class="main-table__check"></td>
                        <td colspan="7">
                            <a @click="addTask" class="link-add link-add--gray">Add a task</a>
                        </td>
                        <td class="main-table__empty-col"></td>
                    </tr>
                    </tbody>
                </table>

                <TablePager
                    v-if="items.length"
                    :pages-count="gridParams.pagesCount"
                    :init-page="gridParams.currentPage"
                    @changePage="gridMethods.changePage"
                />
            </template>

            <template v-else>
                <div class="project-empty">
                    <h2 class="project-empty__title">No Tasks</h2>

                    <div class="project-empty__add">
                        <!-- <input type="text" name="" placeholder="Add a task"> -->
                        <a @click="addTask" class="link-add link-add--gray">Add a task</a>
                    </div>
                </div>
            </template>
        </template>
    </div>
</template>

<style scoped>

</style>
